/**
 * ContactSelector - Seletor de contato com busca via RPC
 * Componente reutilizável para selecionar contatos
 * Exibe dados do CLIENTE quando vinculado, senão dados do contato
 * 
 * Versão: 2.0.0
 * - Migrado para RPC search_contacts_for_selector (segurança por abstração)
 * - LEFT JOIN com crm_customers via server-side
 * - Badge PF/PJ para contatos com cliente vinculado
 * 
 * RELEASE: 2026-09-26 v2.1.1 - Tipo do retorno completo exportado para filtros reutilizáveis.
 * RELEASE: 2026-08-21 v2.1.0 - Nova prop opcional `onSelectContact` que devolve o contato
 *            completo (inclui customer_id) para permitir escopos derivados no chamador
 * RELEASE: 2026-04-07 v2.0.0 - Migração para RPC server-side
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { formatPhoneForDisplay } from '@/lib/phone-utils';

// Interface do contato com dados do cliente vinculado
export interface ContactSelectorContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  // Dados do cliente vinculado (se existir)
  customer_id: string | null;
  customer_name: string | null;
  customer_type: 'pf' | 'pj' | null;
}

// Props do componente
interface ContactSelectorProps {
  value?: string | null;
  onSelect: (contactId: string | null) => void;
  /** v2.1.0 — devolve o contato completo (customer_id/customer_name) ao selecionar */
  onSelectContact?: (contact: ContactSelectorContact | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Se true, exclui contatos que já são main_contact de um crm_customer */
  excludeLinkedToCustomer?: boolean;
}

/**
 * Componente para selecionar contatos com busca via RPC server-side
 */
export function ContactSelector({
  value,
  onSelect,
  onSelectContact,
  placeholder = 'Selecione um contato...',
  disabled = false,
  className,
  excludeLinkedToCustomer = false,
}: ContactSelectorProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Busca contatos via RPC server-side
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts-selector', currentTenant?.id, search, excludeLinkedToCustomer],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      const { data, error } = await supabase.rpc('search_contacts_for_selector', {
        p_search: search || null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;
      
      const processedContacts = (data || []) as ContactSelectorContact[];
      
      // Filtra contatos já vinculados a clientes (se solicitado)
      if (excludeLinkedToCustomer) {
       return processedContacts.filter(c => !c.customer_id);
      }
      
      return processedContacts;
    },
    enabled: !!currentTenant?.id && open,
  });

  // Busca contato selecionado via RPC (para exibição)
  const { data: selectedContact } = useQuery({
    queryKey: ['contact-with-customer', value],
    queryFn: async () => {
      if (!value) return null;
      const { data, error } = await supabase.rpc('search_contacts_for_selector', {
        p_contact_id: value,
      });
      
      if (error || !data || data.length === 0) return null;
      return data[0] as ContactSelectorContact;
    },
    enabled: !!value,
  });

  // Formata exibição do contato (prioriza dados do cliente se existir)
  const formatContact = (contact: ContactSelectorContact | null) => {
    if (!contact) return placeholder;
    
    // Se tem cliente vinculado, exibe nome do cliente
    if (contact.customer_id && contact.customer_name) {
      const parts = [contact.customer_name];
      if (contact.phone) parts.push(formatPhoneForDisplay(contact.phone));
      return parts.filter(Boolean).join(' • ');
    }
    
    // Fallback para dados do contato
    const parts = [contact.name];
    if (contact.phone) parts.push(formatPhoneForDisplay(contact.phone));
    return parts.filter(Boolean).join(' • ');
  };

  // Retorna o nome de exibição (cliente ou contato)
  const getDisplayName = (contact: ContactSelectorContact) => {
    if (contact.customer_id && contact.customer_name) {
      return contact.customer_name;
    }
    return contact.name || 'Sem nome';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedContact ? (
              <>
                <SmartAvatar 
                  src={selectedContact.avatar_url}
                  name={selectedContact.name}
                  size="xs"
                  className="h-5 w-5"
                  defaultFallback="C"
                />
                <span className="truncate">{formatContact(selectedContact)}</span>
              </>
            ) : (
              <>
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{placeholder}</span>
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar contato..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Buscando...' : 'Nenhum contato encontrado.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Opção para limpar seleção */}
              {value && (
                <CommandItem
                  value="clear"
                  onSelect={() => {
                    onSelect(null);
                    onSelectContact?.(null);
                    setOpen(false);
                  }}
                  className="text-muted-foreground"
                >
                  <User className="mr-2 h-4 w-4" />
                  Nenhum contato
                </CommandItem>
              )}
              {contacts.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={contact.id}
                  onSelect={() => {
                    onSelect(contact.id);
                    onSelectContact?.(contact);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2 flex-1">
                    <SmartAvatar 
                      src={contact.avatar_url}
                      name={getDisplayName(contact)}
                      size="xs"
                      defaultFallback={contact.customer_id ? 'C' : 'U'}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      {/* Nome principal com badge se for cliente */}
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium">
                          {getDisplayName(contact)}
                        </span>
                        {contact.customer_id && (
                          <Badge 
                            variant="secondary" 
                            className="text-[9px] px-1 py-0 h-4 shrink-0"
                          >
                            {contact.customer_type === 'pj' ? 'PJ' : 'PF'}
                          </Badge>
                        )}
                      </div>
                      {/* Telefone e email */}
                      <span className="text-xs text-muted-foreground truncate">
                        {[formatPhoneForDisplay(contact.phone), contact.email].filter(Boolean).join(' • ')}
                      </span>
                    </div>
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4',
                      value === contact.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
